/** Utilidades compartidas do módulo de correo (Mailbox + FamilyMailbox). */

export type MailboxAttachmentMeta = {
  index: number;
  filename: string;
  mime_type?: string;
  size?: number | null;
  previewable?: boolean;
};

export type MailboxReplyDetail = {
  uid: number;
  folder?: string;
  subject?: string;
  from?: string;
  date?: string | null;
  from_emails?: string[];
  reply_to_emails?: string[];
  body_html?: string | null;
  body_text?: string | null;
};

export function formatMailboxDate(
  value: string | null | undefined,
  localeCode: string
): string {
  if (!value) return "—";
  const d = new Date(value.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(localeCode === "es" ? "es-ES" : "gl-ES", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatMailboxSize(
  bytes: number | null | undefined,
  empty = "—"
): string {
  if (!bytes) return empty;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function isMailboxPreviewable(att: MailboxAttachmentMeta): boolean {
  if (att.previewable) return true;
  const mime = (att.mime_type || "").toLowerCase();
  if (mime.startsWith("image/") || mime === "application/pdf") return true;
  const ext = att.filename.split(".").pop()?.toLowerCase() || "";
  return ["pdf", "jpg", "jpeg", "png", "gif", "webp"].includes(ext);
}

export function resolveMailboxPreviewMime(
  att: MailboxAttachmentMeta,
  blobType?: string
): string {
  const candidates = [att.mime_type, blobType, ""].map((m) =>
    (m || "").toLowerCase().trim()
  );
  for (const mime of candidates) {
    if (mime.startsWith("image/") || mime === "application/pdf") return mime;
  }
  const ext = att.filename.split(".").pop()?.toLowerCase() || "";
  if (ext === "pdf") return "application/pdf";
  if (["jpg", "jpeg"].includes(ext)) return "image/jpeg";
  if (ext === "png") return "image/png";
  if (ext === "gif") return "image/gif";
  if (ext === "webp") return "image/webp";
  return candidates[0] || "application/octet-stream";
}

function escapeTextAsHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br>");
}

/** Constrúe o HTML citado dunha resposta (blockquote). */
export function buildReplyQuoteHtml(
  detail: MailboxReplyDetail,
  quoteHeaderLabel: string
): string {
  const quoteHtml =
    detail.body_html?.trim() ||
    (detail.body_text ? escapeTextAsHtml(detail.body_text) : "");
  return (
    `<p><br></p><p><br></p>` +
    `<blockquote style="margin:0;padding:12px 16px;border-left:3px solid #94a3b8;color:#475569;background:#f8fafc;">` +
    `<p style="margin:0 0 8px;font-size:13px;"><strong>${quoteHeaderLabel}</strong></p>` +
    `<div>${quoteHtml || "—"}</div>` +
    `</blockquote>`
  );
}

export function resolveReplyTo(detail: MailboxReplyDetail): string {
  return (
    (detail.reply_to_emails || []).join(", ") ||
    (detail.from_emails || []).join(", ") ||
    ""
  );
}

export function resolveReplySubject(subject: string | undefined): string {
  const value = subject || "";
  return /^re\s*:/i.test(value) ? value : `Re: ${value}`;
}
